import { useMutation, useQueryClient } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import { extractApiError } from '@/hooks/mutations/utils';
import type { Customer } from '@/hooks/queries/customer';
import { isWeb } from '@/utils/getPlatform';
import { Logger } from '@/utils/logging';

import type {
  UploadProfileImageRequest,
  UploadProfileImageResponse,
  UseUploadProfileImageMutationOptions,
} from './types';

const convertUriToFile = async (
  uri: string,
  filename: string,
  mimeType: string,
): Promise<File> => {
  const response = await fetch(uri);
  const blob = await response.blob();
  return new File([blob], filename, { type: mimeType });
};

const uploadProfileImage = async (
  requestData: UploadProfileImageRequest,
): Promise<UploadProfileImageResponse> => {
  try {
    const formData = new FormData();

    const filename = requestData.imageUri.split('/').pop();
    const match = /\.(\w+)$/.exec(filename || '');
    const type = match ? `image/${match[1]}` : 'image/jpeg';

    formData.append('enabled', 'true');

    if (isWeb()) {
      const file = await convertUriToFile(
        requestData.imageUri,
        filename || `${requestData.customerId}.jpg`,
        type,
      );
      formData.append('image', file);
    } else {
      formData.append('image', {
        uri: requestData.imageUri,
        name: filename || `${requestData.customerId}.jpg`,
        type,
      } as unknown as Blob);
    }

    const { data } = await apiClient.post<UploadProfileImageResponse>(
      `/v1/customer/${requestData.customerId}/photo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return data;
  } catch (error: unknown) {
    Logger.error('Error uploading profile image', { error });
    throw extractApiError(error, 'UPLOAD_ERROR');
  }
};

export const useUploadProfileImageMutation = (
  options?: UseUploadProfileImageMutationOptions,
) => {
  const queryClient = useQueryClient();

  // Pull out the callbacks we augment so the spread below can't replace the
  // internal optimistic rollback / cache invalidation. Consumer callbacks are
  // invoked after the internal logic runs.
  const { onError, onSettled, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: uploadProfileImage,
    onMutate: async (newImageData) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['user'] });

      // Snapshot the previous value
      const previousUserData = queryClient.getQueryData<Customer | null>([
        'user',
      ]);

      // Optimistically update to show the new image
      queryClient.setQueryData<Customer | null>(
        ['user'],
        (old: Customer | null | undefined) => {
          if (!old) return old;

          return {
            ...old,
            photos: [
              ...(old.photos || []),
              {
                id: 'temp-upload',
                image: newImageData.imageUri,
                thumbnailImage: newImageData.imageUri,
                url: newImageData.imageUri,
                thumbnail: newImageData.imageUri,
                order: 0,
                enabled: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                customer_photos: {
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  customerId: old.id,
                  photoId: 'temp-upload',
                },
              },
            ],
          };
        },
      );

      // Return a context object with the snapshotted value
      return { previousUserData };
    },
    onError: (...errorArgs) => {
      // Rollback to the previous value on error
      const context = errorArgs[2];
      if (context?.previousUserData) {
        queryClient.setQueryData(['user'], context.previousUserData);
      }
      onError?.(...errorArgs);
    },
    onSettled: (...settledArgs) => {
      const variables = settledArgs[2];
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({
        queryKey: ['customer', variables.customerId],
      });
      onSettled?.(...settledArgs);
    },
    retry: (failureCount, error) => {
      const nonRetryableCodes = [
        'VALIDATION_ERROR',
        'INVALID_FILE_TYPE',
        'FILE_TOO_LARGE',
      ];
      if (nonRetryableCodes.includes(error.code)) return false;
      return failureCount < 2;
    },
    ...restOptions,
  });
};
